<script setup lang="ts">
import { type Ref, computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useLanguageService } from '@/composables/language'
import { useWindowWidth } from '@/utils'
import messagesData from '@/utils/5_second.json'

type MessageType = 'loading' | 'unloading' | 'order'

interface DataItem {
  message: string
  type: MessageType
}

const languageService = useLanguageService()
const currentLang = languageService.currentLanguage
const windowWidth = useWindowWidth()

const currentMessageSet = computed<DataItem[]>(() => {
  if (currentLang.value === 'de' && messagesData.de)
    return messagesData.de as DataItem[]

  return (messagesData.en || []) as DataItem[]
})

const currentDataLength = computed(() => currentMessageSet.value.length)

function useRandomIndex(lengthRef: Ref<number>) {
  const randomIndex = ref(0)

  const setCorrectRandomIndex = () => {
    if (lengthRef.value > 0)
      randomIndex.value = Math.floor(Math.random() * lengthRef.value)
    else
      randomIndex.value = 0
  }

  setCorrectRandomIndex()

  const updateRandomIndex = () => {
    setCorrectRandomIndex()
  }

  watch(lengthRef, (newLength, oldLength) => {
    if (newLength !== oldLength)
      setCorrectRandomIndex()
  })

  return { randomIndex, updateRandomIndex }
}

const { randomIndex: index1, updateRandomIndex: updateIndex1 } = useRandomIndex(currentDataLength)
const { randomIndex: index2, updateRandomIndex: updateIndex2 } = useRandomIndex(currentDataLength)
const { randomIndex: index3, updateRandomIndex: updateIndex3 } = useRandomIndex(currentDataLength)

const visibleTexts = computed(() => {
  const dataList: DataItem[] = []
  const messages = currentMessageSet.value
  const length = messages.length

  if (length === 0)
    return []

  if (windowWidth.value <= 768) {
    dataList.push(messages[index1.value % length] as DataItem)
  }
  else {
    dataList.push(messages[index1.value % length] as DataItem)
    if (length > 1)
      dataList.push(messages[index2.value % length] as DataItem)

    if (length > 2)
      dataList.push(messages[index3.value % length] as DataItem)
  }

  return dataList.filter(item => !!item)
})

let intervalId: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  intervalId = setInterval(() => {
    if (currentDataLength.value === 0)
      return

    if (windowWidth.value <= 768) {
      updateIndex1()
    }
    else {
      updateIndex1()
      if (currentDataLength.value > 1)
        updateIndex2()
      if (currentDataLength.value > 2)
        updateIndex3()
    }
  }, 9000)
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <div class="hero-messages">
    <TransitionGroup name="hero-slide" tag="div" class="bubble-row">
      <div v-for="text in visibleTexts" :key="text.message" class="bubble-card">
        <div class="bubble-content">
          <span class="icon-circle">
            <svg
              v-if="text.type === 'order'"
              class="icon-svg"
              width="18"
              height="20"
              viewBox="0 0 18 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.8585 2.49938C16.7317 2.37338 16.5825 2.31 16.41 2.31C16.3241 2.31 16.143 2.3805 15.8677 2.51963C15.5921 2.65913 15.2996 2.81438 14.9917 2.98763C14.6831 3.15975 14.3179 3.31575 13.8964 3.45525C13.4752 3.59438 13.0852 3.66488 12.7264 3.66488C12.3949 3.66488 12.1024 3.60188 11.8504 3.4755C11.0145 3.084 10.2874 2.79188 9.67012 2.59913C9.05287 2.40675 8.38912 2.31 7.6785 2.31C6.4515 2.31 5.05387 2.709 3.48712 3.5055C3.16387 3.66675 2.93213 3.79163 2.78212 3.88313L2.63325 2.7915C2.96962 2.49938 3.18675 2.07375 3.18675 1.59337C3.18675 0.71325 2.4735 0 1.59338 0C0.71325 0 0 0.71325 0 1.59337C0 2.1675 0.30675 2.66663 0.762 2.94713L2.9145 18.7309C2.97862 19.2008 3.38062 19.5416 3.84225 19.5416C3.88425 19.5416 3.927 19.539 3.97012 19.5334C4.48312 19.4629 4.84237 18.9908 4.77225 18.4778L3.90712 12.1339C5.47687 11.355 6.83475 10.9624 7.977 10.9624C8.46187 10.9624 8.93475 11.0348 9.39562 11.181C9.85687 11.3276 10.2405 11.4866 10.5457 11.6588C10.851 11.8313 11.2114 11.991 11.6257 12.1365C12.0409 12.2828 12.4507 12.3559 12.8554 12.3559C13.8776 12.3559 15.102 11.9708 16.5289 11.2009C16.7081 11.1079 16.8394 11.0164 16.9222 10.9268C17.0051 10.8371 17.0467 10.7096 17.0467 10.5439V2.9475C17.0467 2.77538 16.9841 2.62575 16.8585 2.49938Z"
                fill="#D81B5D"
              />
            </svg>
            <svg
              v-else-if="text.type === 'loading'"
              class="icon-svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.4487 8.41284H9.21894V4.55371C9.21894 4.13971 8.88294 3.80371 8.46894 3.80371C8.05494 3.80371 7.71894 4.13971 7.71894 4.55371V8.41284H5.48919C5.04706 8.41284 4.94469 8.66334 5.26044 8.97234L8.46819 12.116L11.6778 8.97234C11.9936 8.66334 11.8912 8.41284 11.4487 8.41284Z"
                fill="#D81B5D"
              />
              <path
                d="M21.1347 16.2254H21.0065V11.5795C21.0065 11.3676 20.9026 11.0594 20.7747 10.891L18.6496 8.09687C18.5213 7.92887 18.2457 7.79199 18.0338 7.79199H15.0717H14.3877H14.1987C13.7847 7.79199 13.4487 8.12762 13.4487 8.54199V14.2506H4.11421V11.3204C4.11421 10.8722 3.75084 10.5089 3.30271 10.5089C2.85459 10.5089 2.49121 10.8722 2.49121 11.3204V16.6236H2.49946V17.9417C2.49946 18.1532 2.67121 18.3254 2.88271 18.3254H4.19221C4.19221 16.8801 5.36784 15.7041 6.81271 15.7041C8.25796 15.7041 9.43359 16.8801 9.43359 18.3254H14.3255C14.3255 16.8801 15.5011 15.7045 16.946 15.7045C18.3912 15.7045 19.5668 16.8801 19.5668 18.3254H21.1257C21.338 18.3254 21.5093 18.1532 21.5093 17.9417V16.6004C21.5097 16.393 21.3421 16.2254 21.1347 16.2254ZM15.1778 11.4842V9.24512H17.1496C17.3513 9.24512 17.6131 9.37637 17.7342 9.53799L19.1562 11.4842H15.1778Z"
                fill="#D81B5D"
              />
              <path
                d="M6.81288 16.4541C5.77938 16.4541 4.94238 17.2919 4.94238 18.3254C4.94238 19.3589 5.77938 20.1959 6.81288 20.1959C7.84676 20.1959 8.68376 19.3589 8.68376 18.3254C8.68376 17.2922 7.84638 16.4541 6.81288 16.4541ZM6.81288 18.9471C6.47013 18.9471 6.19151 18.6689 6.19151 18.3257C6.19151 17.9819 6.47013 17.7036 6.81288 17.7036C7.15676 17.7036 7.43538 17.9819 7.43538 18.3257C7.43538 18.6689 7.15638 18.9471 6.81288 18.9471Z"
                fill="#D81B5D"
              />
              <path
                d="M16.9457 16.4541C15.9126 16.4541 15.0752 17.2919 15.0752 18.3254C15.0752 19.3589 15.9126 20.1959 16.9457 20.1959C17.9796 20.1959 18.8166 19.3589 18.8166 18.3254C18.8166 17.2922 17.9796 16.4541 16.9457 16.4541ZM16.9457 18.9471C16.6026 18.9471 16.3239 18.6689 16.3239 18.3257C16.3239 17.9819 16.6026 17.7036 16.9457 17.7036C17.2892 17.7036 17.5678 17.9819 17.5678 18.3257C17.5678 18.6689 17.2892 18.9471 16.9457 18.9471Z"
                fill="#D81B5D"
              />
            </svg>
            <svg
              v-else
              class="icon-svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.48938 7.50684H7.71913V11.366C7.71913 11.78 8.05513 12.116 8.46913 12.116C8.88313 12.116 9.21913 11.78 9.21913 11.366V7.50684H11.4489C11.891 7.50684 11.9934 7.25634 11.6776 6.94734L8.46988 3.80371L5.26025 6.94734C4.94488 7.25634 5.04688 7.50684 5.48938 7.50684Z"
                fill="#D81B5D"
              />
              <path
                d="M21.1347 16.2254H21.0065V11.5795C21.0065 11.3676 20.9026 11.0594 20.7747 10.891L18.6496 8.09687C18.5213 7.92887 18.2457 7.79199 18.0338 7.79199H15.0717H14.3877H14.1987C13.7847 7.79199 13.4487 8.12762 13.4487 8.54199V14.2506H4.11421V11.3204C4.11421 10.8722 3.75084 10.5089 3.30271 10.5089C2.85459 10.5089 2.49121 10.8722 2.49121 11.3204V16.6236H2.49946V17.9417C2.49946 18.1532 2.67121 18.3254 2.88271 18.3254H4.19221C4.19221 16.8801 5.36784 15.7041 6.81271 15.7041C8.25796 15.7041 9.43359 16.8801 9.43359 18.3254H14.3255C14.3255 16.8801 15.5011 15.7045 16.946 15.7045C18.3912 15.7045 19.5668 16.8801 19.5668 18.3254H21.1257C21.338 18.3254 21.5093 18.1532 21.5093 17.9417V16.6004C21.5097 16.393 21.3421 16.2254 21.1347 16.2254ZM15.1778 11.4842V9.24512H17.1496C17.3513 9.24512 17.6131 9.37637 17.7342 9.53799L19.1562 11.4842H15.1778Z"
                fill="#D81B5D"
              />
              <path
                d="M6.81288 16.4541C5.77938 16.4541 4.94238 17.2919 4.94238 18.3254C4.94238 19.3589 5.77938 20.1959 6.81288 20.1959C7.84676 20.1959 8.68376 19.3589 8.68376 18.3254C8.68376 17.2922 7.84638 16.4541 6.81288 16.4541ZM6.81288 18.9471C6.47013 18.9471 6.19151 18.6689 6.19151 18.3257C6.19151 17.9819 6.47013 17.7036 6.81288 17.7036C7.15676 17.7036 7.43538 17.9819 7.43538 18.3257C7.43538 18.6689 7.15638 18.9471 6.81288 18.9471Z"
                fill="#D81B5D"
              />
              <path
                d="M16.9457 16.4541C15.9126 16.4541 15.0752 17.2919 15.0752 18.3254C15.0752 19.3589 15.9126 20.1959 16.9457 20.1959C17.9796 20.1959 18.8166 19.3589 18.8166 18.3254C18.8166 17.2922 17.9796 16.4541 16.9457 16.4541ZM16.9457 18.9471C16.6026 18.9471 16.3239 18.6689 16.3239 18.3257C16.3239 17.9819 16.6026 17.7036 16.9457 17.7036C17.2892 17.7036 17.5678 17.9819 17.5678 18.3257C17.5678 18.6689 17.2892 18.9471 16.9457 18.9471Z"
                fill="#D81B5D"
              />
            </svg>
          </span>
          <span class="bubble-text">{{ text.message }}</span>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.hero-messages {
  position: relative;
  min-height: 80px;
  z-index: 2;
}

.bubble-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 12px;
  position: relative;
  z-index: 2;
}

.bubble-card {
  background: #ffffff;
  border-radius: 18px;
  flex: 1 1 0;
  min-width: 240px;
  max-width: 420px;
  box-shadow:
    0px 0px 0px 0px #14151f0d,
    0px 8px 17px 0px #14151f0d,
    0px 30px 30px 0px #14151f0a,
    0px 68px 41px 0px #14151f08,
    0px 120px 48px 0px #14151f03,
    0px 188px 53px 0px #14151f00;
}

.bubble-content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
}

.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 47px;
  padding: 12px;
  background: #f1f2f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-svg {
  height: 17px;
  width: auto;
  fill: #d81b5d;
}

.bubble-text {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0%;
  color: #18191f;
}

.hero-slide-enter-from {
  transform: translateX(110%);
  opacity: 0;
}

.hero-slide-leave-to {
  transform: translateX(-80%);
  opacity: 0;
}

.hero-slide-enter-active,
.hero-slide-move,
.hero-slide-leave-active {
  transition: all 1.8s ease-out;
}

.hero-slide-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

@media (max-width: 768px) {
  .bubble-row {
    gap: 10px;
  }

  .bubble-card {
    min-width: 0;
    width: 100%;
  }
}
</style>
